import { useState } from "react";
import { Check, ChevronDown, ChevronUp, Pencil, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import type { OrganizationType, PermissionKey } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { ROLE_LABEL } from "@/components/team/role-meta";
import { gruposPorEscopo } from "@/components/permissions/permission-scopes";

type CustomRole = {
  id: string;
  label: string;
  basedOnRole: string;
  memberCount: number;
  permissions: { key: string; granted: boolean }[];
};

type Props = {
  role: CustomRole;
  locked?: boolean;
  onDelete: () => void;
};

export function CustomRoleCard({ role, locked = false, onDelete }: Props) {
  const utils = trpc.useUtils();
  const meQuery = trpc.me.current.useQuery();
  const tipos = (meQuery.data?.activeOrganization?.types ?? []) as OrganizationType[];
  const [expanded, setExpanded] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [label, setLabel] = useState(role.label);

  const rename = trpc.organization.customRoles.rename.useMutation({
    onSuccess: () => {
      utils.organization.customRoles.list.invalidate();
      utils.team.list.invalidate();
      toast.success("Papel renomeado.");
      setRenaming(false);
    },
    onError: (err) => toast.error(err.message || "Erro ao renomear."),
  });

  const setPermission = trpc.organization.customRoles.setPermission.useMutation({
    onSuccess: () => utils.organization.customRoles.list.invalidate(),
    onError: (err) => toast.error(err.message || "Erro ao atualizar permissão."),
  });

  const isGranted = (key: string) => role.permissions.find((p) => p.key === key)?.granted ?? false;

  const handleRename = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || label.trim() === role.label) {
      setRenaming(false);
      return;
    }
    rename.mutate({ id: role.id, label: label.trim() });
  };

  return (
    <div className="rounded-lg border border-zinc-200 dark:border-border">
      <div className="flex flex-wrap items-center gap-2.5 px-3 py-2.5">
        {renaming ? (
          <form onSubmit={handleRename} className="flex flex-1 items-center gap-1.5">
            <input
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={() => setRenaming(false)}
              className="h-8 min-w-[160px] flex-1 rounded-md border border-zinc-200 bg-white px-2 text-[13px] text-zinc-900 dark:border-border dark:bg-card dark:text-foreground"
            />
            <button
              type="submit"
              disabled={rename.isPending}
              className="text-emerald-600 hover:text-emerald-700"
            >
              <Check className="h-4 w-4" />
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex flex-1 items-center gap-2 text-left"
          >
            {expanded ? (
              <ChevronUp className="h-4 w-4 text-zinc-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-zinc-400" />
            )}
            <span className="text-sm font-semibold text-zinc-900 dark:text-foreground">
              {role.label}
            </span>
            <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-zinc-600 dark:bg-muted dark:text-foreground/70">
              Baseado em {ROLE_LABEL[role.basedOnRole as keyof typeof ROLE_LABEL] ?? role.basedOnRole}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500 dark:text-muted-foreground">
              <Users className="h-3 w-3" />
              {role.memberCount}
            </span>
          </button>
        )}

        {!renaming && !locked && (
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => setRenaming(true)}
              className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted"
              title="Renomear"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="flex h-7 w-7 items-center justify-center rounded-md text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-500/10"
              title="Excluir"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {expanded && (
        <div className="space-y-4 border-t border-zinc-100 px-3 py-2.5 dark:border-border">
          {/* Mesmo agrupamento e filtro por área da matriz principal — inconsistência aqui ficaria
              a um clique de distância dela. */}
          {gruposPorEscopo(tipos).map(({ scope, label: rotuloDaArea, groups }) => (
            <div key={scope} className="space-y-2.5">
              <p className="text-[10.5px] font-bold uppercase tracking-wider text-brand-800 dark:text-primary">
                {rotuloDaArea}
              </p>
              {groups.map((group) => (
            <div key={group.resource}>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
                {group.label}
              </p>
              <div className="space-y-1">
                {group.keys.map(({ key, label: keyLabel }) => (
                  <label
                    key={key}
                    className="flex items-center gap-2 text-[12.5px] text-zinc-700 dark:text-foreground/80"
                  >
                    <input
                      type="checkbox"
                      checked={isGranted(key)}
                      disabled={locked || setPermission.isPending}
                      onChange={(e) =>
                        setPermission.mutate({
                          customRoleId: role.id,
                          permissionKey: key as PermissionKey,
                          granted: e.target.checked,
                        })
                      }
                      className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-border"
                    />
                    {keyLabel}
                  </label>
                ))}
              </div>
            </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
